// Two switchable color palettes for the demo. `brandColors` / `brandSemantic`
// are the live, mutable objects every component reads from (imported by
// reference all over the app) — switching palettes mutates their properties
// in place via `applyPalette`, so a re-render after switching is all that's
// needed to repaint the whole app with the new colors.

const defaultPalette = {
  name: 'default',
  label: 'Default',
  brandColors: {
    mocca: '#afa89f',
    rose: '#e5d4d3',
    dustyGreen: '#cfdfd6',
    ocre: '#e8d49a',
    turkis: '#88d1d1',
  },
  brandSemantic: {
    primary: '#8F867C',
    primaryHover: '#756C63',
    primarySoft: '#F5F2EE',
    primaryMuted: '#D8D2CB',
    secondary: '#2D8790',
    secondaryHover: '#256F76',
    secondarySoft: '#E5F2F3',
    secondaryMuted: '#C4D9DB',
    linkColor: '#2D8790',
    linkColorHover: '#256F76',
    roseSoft: '#FAF5F5',
    roseSelected: '#e5d4d3',
    tableHoverBg: '#FAF5F5',
    tableHeaderColor: '#2F2B28',
    tableHeaderBg: '#F5F2EE',
    success: '#6F8A78',
    successSoft: '#F1F8F4',
    successBorder: '#BFD5C8',
    warning: '#987D3A',
    warningSoft: '#FCF7E6',
    warningBorder: '#DFCA86',
    info: '#497F80',
    infoSoft: '#EDF9F9',
    infoBorder: '#A9DDDD',
    error: '#9A6463',
    errorSoft: '#FBF1F1',
    errorBorder: '#E2C2C1',
    text: '#2F2B28',
    textSecondary: '#716A64',
    textTertiary: '#AFA89F',
    border: '#E6DFDD',
    borderSecondary: '#F0E8E6',
    layout: '#FBF8F6',
    surface: '#FFFFFF',
    // Sidebar (default theme keeps the light sidebar look)
    sidebarBg: '#FFFFFF',
    sidebarHeaderBg: '#FFFFFF',
    sidebarBorder: '#F0E8E6',
    sidebarIconColor: '#716A64',
    sidebarIconActiveColor: '#2F2B28',
    sidebarLabelColor: '#716A64',
    sidebarLabelActiveColor: '#2F2B28',
    sidebarItemHoverBg: '#F5F2EE',
    sidebarItemActiveBg: '#e5d4d3',
  },
};

// Profilbureauet CRM color usage guide — warm neutral brand
// with a mid-grey sidebar (see profilbureauet_crm_color_usage.md).
const profilbureauetPalette = {
  name: 'profilbureauet',
  label: 'Profilbureauet',
  brandColors: {
    mocca: '#77736C',
    rose: '#FBE4E6',
    dustyGreen: '#E5EEE4',
    ocre: '#F8EBD4',
    turkis: '#E5EDF1',
  },
  brandSemantic: {
    primary: '#C9A97B',
    primaryHover: '#B8945F',
    primarySoft: '#F3E8D8',
    primaryMuted: '#D9D5CD',
    secondary: '#BEB8B7',
    secondaryHover: '#F2E3E0',
    secondarySoft: '#FBE4E6',
    secondaryMuted: '#E2AFB2',
    linkColor: '#333333',
    linkColorHover: '#AFA89F',
    roseSoft: '#FBE4E6',
    roseSelected: '#FBE4E6',
    tableHoverBg: '#F9EEED',
    tableHeaderColor: '#373535',
    tableHeaderBg: '#AFA89F',
    success: '#6F8A72',
    successSoft: '#E5EEE4',
    successBorder: '#C3D6C5',
    warning: '#C98A2E',
    warningSoft: '#F8EBD4',
    warningBorder: '#E6C98E',
    info: '#55758A',
    infoSoft: '#E5EDF1',
    infoBorder: '#B9CDD7',
    error: '#B4232E',
    errorSoft: '#FBE4E6',
    errorBorder: '#E3AFB4',
    text: '#1C1C1C',
    textSecondary: '#454545',
    textTertiary: '#77736C',
    border: '#D9D5CD',
    borderSecondary: '#EAE6DF',
    layout: '#F7F5F0',
    surface: '#FFFFFF',
    sidebarBg: '#777271',
    sidebarHeaderBg: '#777271',
    sidebarBorder: '#777271',
    sidebarIconColor: '#EDEBE6',
    sidebarIconActiveColor: '#FFFFFF',
    sidebarLabelColor: '#EDEBE6',
    sidebarLabelActiveColor: '#FFFFFF',
    sidebarItemHoverBg: '#5E5A59',
    sidebarItemActiveBg: '#5E5A59',
  },
};

export const PALETTES = {
  [defaultPalette.name]: defaultPalette,
  [profilbureauetPalette.name]: profilbureauetPalette,
};

export const brandColors = { ...defaultPalette.brandColors };
export const brandSemantic = { ...defaultPalette.brandSemantic };

let activePaletteName = defaultPalette.name;

export const getActivePaletteName = () => activePaletteName;

export const applyPalette = (name) => {
  const palette = PALETTES[name] || defaultPalette;
  Object.assign(brandColors, palette.brandColors);
  Object.assign(brandSemantic, palette.brandSemantic);
  activePaletteName = palette.name;
};
